export const LINKEDIN_LIMIT = 1300;

export type ToneId = "casual" | "leader" | "data" | "growth";
export type AudienceId = "founders" | "engineers" | "marketers" | "execs" | "careers";
export type LengthId = "punchy" | "balanced" | "longform";

export interface Tone {
  id: ToneId;
  label: string;
  blurb: string;
  /** How the same voice should read to the model writing the posts. */
  voice: string;
  bullet: string;
  bridge: string;
  cta: string;
  sign: string;
  tags: string[];
  hook: (line: string) => string;
}

export const TONES: Tone[] = [
  {
    id: "casual",
    label: "Casual / Witty",
    blurb: "Conversational, self-aware, light on jargon.",
    voice:
      "Conversational and a little self-deprecating. Short sentences, contractions, the odd aside in brackets. No corporate vocabulary, no hype. One emoji at most, and only near the call to action.",
    bullet: "→",
    bridge: "Here's the unglamorous version:",
    cta: "Which one hits closest to home? I'll go first in the comments \u{1F447}",
    sign: "We recorded this twice. The first take was too polite.",
    tags: ["#Podcasting", "#LessonsLearned", "#BuildInPublic"],
    hook: (line) => `Not gonna lie — ${lowerFirst(line)}`,
  },
  {
    id: "leader",
    label: "Thought Leader",
    blurb: "Measured, senior, opinion backed by reasoning.",
    voice:
      "Measured and senior. States an opinion and then the reasoning behind it. Calm, specific, no exclamation marks, no hype words, no emoji. Will concede a counter-argument where one genuinely sharpens the point, but never as a closing habit.",
    bullet: "•",
    bridge: "Three things worth sitting with:",
    cta: "If you're navigating the same decision, I'd genuinely value your read in the comments.",
    sign: "Full conversation is linked in the first comment.",
    tags: ["#Leadership", "#Strategy", "#Podcast"],
    hook: (line) => upperFirst(line),
  },
  {
    id: "data",
    label: "Scientific / Data-Driven",
    blurb: "Evidence first, claims qualified, numbers up front.",
    voice:
      "Evidence first. Leads with a number or a measurement when the transcript supplies one, and qualifies claims the transcript does not support. Precise, unexcited, no emoji. Never invents a statistic.",
    bullet: "▸",
    bridge: "What the evidence actually supports:",
    cta: "Curious where your own numbers land — drop them below and I'll compare notes.",
    sign: "Caveat: one conversation, not a controlled study. Treat it as a hypothesis.",
    tags: ["#DataDriven", "#Analytics", "#ProductStrategy"],
    hook: (line) =>
      `${upperFirst(line)} The data is far less ambiguous than the debate around it.`,
  },
  {
    id: "growth",
    label: "Aggressive / Growth",
    blurb: "Direct, urgent, built around a play you can run today.",
    voice:
      "Direct and urgent. Every post hands the reader something to run this week. Imperative verbs, short lines, zero hedging. At most one emoji, at the call to action. Confident without being a caricature.",
    bullet: "↳",
    bridge: "Run this play:",
    cta: "Steal it. Ship it this week. Then tell me what broke \u{1F447}",
    sign: "Save this for your next planning cycle.",
    tags: ["#Growth", "#GTM", "#Execution"],
    hook: (line) =>
      `${upperFirst(line)} Most people will read this and change nothing. Don't be most people.`,
  },
];

export interface Audience {
  id: AudienceId;
  label: string;
  short: string;
  line: string;
  tag: string;
}

export const AUDIENCES: Audience[] = [
  {
    id: "founders",
    label: "Founders & Operators",
    short: "founders",
    line: "If you're running a small team with no margin for error, this is the part that matters.",
    tag: "#Founders",
  },
  {
    id: "engineers",
    label: "Engineers & Builders",
    short: "engineers",
    line: "If you ship the thing rather than present the slide about the thing, start here.",
    tag: "#Engineering",
  },
  {
    id: "marketers",
    label: "Marketers & Creators",
    short: "marketers",
    line: "If your job is attention, this is the uncomfortable bit you can act on tomorrow.",
    tag: "#Marketing",
  },
  {
    id: "execs",
    label: "Execs & Investors",
    short: "operators and investors",
    line: "If you're allocating capital or headcount against this, the second-order effect is the real story.",
    tag: "#Leadership",
  },
  {
    id: "careers",
    label: "Job Seekers & Career Switchers",
    short: "people mid-pivot",
    line: "If you're mid-pivot right now, this is the leverage nobody puts in a job description.",
    tag: "#CareerGrowth",
  },
];

export interface LengthOption {
  id: LengthId;
  label: string;
  limit: number;
  rich: boolean;
}

export const LENGTHS: LengthOption[] = [
  { id: "punchy", label: "Punchy (≈ 600 chars)", limit: 600, rich: false },
  { id: "balanced", label: "Balanced (≈ 950 chars)", limit: 950, rich: true },
  { id: "longform", label: "Long-form (≈ 1,300 chars)", limit: LINKEDIN_LIMIT, rich: true },
];

export type AccentId =
  | "emerald"
  | "indigo"
  | "cyan"
  | "violet"
  | "amber"
  | "rose"
  | "sky"
  | "teal"
  | "fuchsia"
  | "lime";

export const ARCHETYPE_IDS = [
  "hook-insights-cta",
  "contrarian",
  "story",
  "framework",
  "myths",
  "data-drop",
  "pull-quote",
  "mistake",
  "rapid-fire",
  "open-question",
] as const;

export type ArchetypeId = (typeof ARCHETYPE_IDS)[number];

export interface Context {
  topic: string;
  theme: string;
  insights: string[];
  keywords: string[];
  stat: string;
  quote: string;
  duration: string;
  rich: boolean;
  limit: number;
}

export interface Archetype {
  id: ArchetypeId;
  label: string;
  note: string;
  accent: AccentId;
  /** Instructions for the model when generation runs against Claude. */
  brief: string;
  /** Offline template used when no API key is configured or the request fails. */
  build: (ctx: Context, tone: Tone, audience: Audience) => string[];
}

export const ARCHETYPES: Archetype[] = [
  {
    id: "hook-insights-cta",
    label: "Hook + 3 Insights + CTA",
    note: "The reliable workhorse: scroll-stopper, then substance.",
    accent: "emerald",
    brief:
      "Open with a one-line hook that would stop a scroll, then a line of setup, then exactly three insights drawn from different parts of the transcript, then a call to action inviting replies.",
    build: (ctx, tone, audience) => [
      tone.hook(
        `we spent ${ctx.duration} on ${ctx.theme} and the honest takeaway was uncomfortable.`,
      ),
      tone.bridge,
      [
        `${tone.bullet} ${ctx.insights[0]}`,
        `${tone.bullet} ${ctx.insights[1]}`,
        `${tone.bullet} ${ctx.insights[2]}`,
      ].join("\n"),
      ctx.rich ? audience.line : "",
      tone.cta,
    ],
  },
  {
    id: "contrarian",
    label: "The Contrarian Take",
    note: "Names the consensus, then argues the other side.",
    accent: "rose",
    brief:
      "Name the conventional wisdom on this topic in one line, then argue against it using what the episode actually showed. Be specific about who believes the consensus and why it fails. No strawman.",
    build: (ctx, tone) => [
      `Unpopular opinion: most of what gets said about ${ctx.theme} comes from people who never had to live with the consequences.`,
      "Everyone agrees you should optimise it. I think that framing is backwards.",
      ctx.insights[3],
      [
        `${tone.bullet} The consensus: do more of it, faster.`,
        `${tone.bullet} What this episode showed: do less of it, and refuse to compromise on the one part that compounds.`,
      ].join("\n"),
      ctx.rich ? ctx.insights[4] : "",
      tone.cta,
    ],
  },
  {
    id: "story",
    label: "The Story-Driven Lesson",
    note: "One scene, one turn, one transferable lesson.",
    accent: "amber",
    brief:
      "Tell one small scene from the recording — a moment, a line that landed, a turn in the conversation — and end on the transferable lesson. Narrative, chronological, one idea only.",
    build: (ctx, tone, audience) => [
      "I almost cut this section from the episode.",
      "We were thirty minutes in, slightly off-script, and then this landed:",
      `"${ctx.quote}"`,
      `That single line reframed how I think about ${ctx.theme} — it moved the problem from effort to design.`,
      ctx.rich ? ctx.insights[5] : "",
      ctx.rich ? audience.line : "",
      tone.cta,
    ],
  },
  {
    id: "framework",
    label: "The Step-by-Step Framework",
    note: "Compresses the episode into a repeatable sequence.",
    accent: "indigo",
    brief:
      "Compress the episode into a numbered sequence of 4-6 steps someone could actually follow. Each step gets a short imperative title and one sentence of substance from the transcript.",
    build: (ctx, tone) => [
      `The ${titleCase(ctx.topic)} Framework — ${ctx.duration} of conversation compressed into 5 steps:`,
      [
        `1. Name the real constraint. ${ctx.insights[0]}`,
        `2. Instrument it before you touch it. ${ctx.insights[1]}`,
        `3. Pick one lever, not five. ${ctx.insights[2]}`,
        `4. Ship the smallest honest test. ${ctx.insights[3]}`,
        "5. Kill it or scale it. There is no third option.",
      ].join("\n\n"),
      ctx.rich ? tone.sign : "",
      tone.cta,
    ],
  },
  {
    id: "myths",
    label: "Myth vs Reality",
    note: "High-contrast listicle. Very screenshot-able.",
    accent: "cyan",
    brief:
      "Four myth/reality pairs, each myth in quotes on its own line prefixed with ❌ and the reality below it prefixed with ✅. The realities come from the transcript; the myths are the beliefs it contradicts.",
    build: (ctx, tone, audience) => [
      `4 myths about ${ctx.theme} — and what actually held up under questioning:`,
      [
        `❌ "It's a volume problem."\n✅ ${ctx.insights[1]}`,
        `❌ "We'll fix it once we scale."\n✅ ${ctx.insights[2]}`,
        `❌ "Everyone already knows this."\n✅ ${ctx.insights[3]}`,
        `❌ "The tooling will solve it."\n✅ ${ctx.insights[4]}`,
      ].join("\n\n"),
      ctx.rich ? audience.line : "",
      tone.cta,
    ],
  },
  {
    id: "data-drop",
    label: "The Data Drop",
    note: "One number as the hook, interpretation underneath.",
    accent: "sky",
    brief:
      "Lead with a single number or measurement that appears in the transcript, on its own line, then interpret what it implies. If the transcript contains no number, lead with the single most concrete claim instead and say plainly that it is a claim, not a measurement. Never invent a figure.",
    build: (ctx, tone) => [
      ctx.stat,
      tone.bridge,
      [`${tone.bullet} ${ctx.insights[2]}`, `${tone.bullet} ${ctx.insights[5]}`].join("\n"),
      ctx.rich
        ? "The uncomfortable implication: most teams measure the output and ignore the input that moves it."
        : "",
      ctx.rich ? tone.sign : "",
      tone.cta,
    ],
  },
  {
    id: "pull-quote",
    label: "The Pull Quote",
    note: "Guest quote plus your commentary. Credibility by proxy.",
    accent: "violet",
    brief:
      "Open with a verbatim quote from the transcript in quotation marks — copy it exactly, do not paraphrase or improve it — then explain in your own words why it stuck with you.",
    build: (ctx, tone) => [
      `"${ctx.quote}"`,
      "That's the line from this week's episode I wrote on a sticky note and left on my monitor.",
      `Why it stuck: it reframes ${ctx.theme} as a design problem rather than an effort problem.`,
      ctx.rich ? ctx.insights[6] : "",
      tone.cta,
    ],
  },
  {
    id: "mistake",
    label: "The Mistake I Made",
    note: "Vulnerability hook. Highest comment rate of the ten.",
    accent: "fuchsia",
    brief:
      "Written in first person as the host: admit a mistake the episode made you see, say what it cost, then what you would do differently. Vulnerable but not self-pitying, and it must resolve into something useful.",
    build: (ctx, tone) => [
      `I got our approach to ${ctx.theme} wrong for two years. Publicly, and expensively.`,
      "The mistake: I treated it as a volume problem when it was always a sequencing problem.",
      ctx.insights[4],
      "What I'd do differently, starting Monday:",
      [`${tone.bullet} ${ctx.insights[0]}`, `${tone.bullet} ${ctx.insights[3]}`].join("\n"),
      tone.cta,
    ],
  },
  {
    id: "rapid-fire",
    label: "Rapid-Fire Lessons",
    note: "Skimmable list, built for saves and reshares.",
    accent: "lime",
    brief:
      "A numbered list of 7 lessons, one line each, no sub-bullets and no elaboration. Each line must stand alone if screenshotted. Order them so the sharpest one lands last.",
    build: (ctx, tone, audience) => [
      `7 things I learned recording an episode about ${ctx.theme}:`,
      [
        `1. ${clip(ctx.insights[0], 110)}`,
        `2. ${clip(ctx.insights[1], 110)}`,
        `3. ${clip(ctx.insights[2], 110)}`,
        `4. ${clip(ctx.insights[3], 110)}`,
        `5. ${clip(ctx.insights[4], 110)}`,
        `6. ${clip(ctx.insights[5], 110)}`,
        "7. The best insight arrived after we thought we were done recording.",
      ].join("\n"),
      ctx.rich ? audience.line : "",
      tone.cta,
    ],
  },
  {
    id: "open-question",
    label: "The Open Question",
    note: "Engagement-first: low effort to answer, high signal.",
    accent: "teal",
    brief:
      "Pose one genuine either/or question the episode raised and did not settle, give the answer the conversation produced, then invite the reader to pick a side. The question must be answerable in one line by a stranger.",
    build: (ctx, tone, audience) => [
      `Genuine question for the ${audience.short} on my feed:`,
      `When your approach to ${ctx.theme} stops working, do you change the system — or change the target?`,
      "I asked this on the podcast and got an answer I didn't expect:",
      ctx.insights[6],
      ctx.rich
        ? "Two camps seem to exist and both have receipts. I'm genuinely not sure which one I'm in."
        : "",
      "Pick a side in the comments — I'm collecting answers for the follow-up episode.",
    ],
  },
];

export const SAMPLE_TRANSCRIPT = `Host: Welcome back to the show. Today we're talking about why most product teams stall right after they find their first real traction.

Guest: Thanks for having me. I'll start with the uncomfortable part. Almost every team I work with is optimising the wrong half of the funnel. They obsess over acquisition because it is visible and easy to measure, while retention quietly decides whether the business still exists in two years.

Host: You said something before we started recording that I want you to repeat. Something about speed.

Guest: Right. Speed is not how fast you ship, it is how fast you can tell whether shipping mattered. Most teams measure velocity and then call it progress. If your feedback loop takes six weeks, you are not moving fast, you are guessing in public with extra steps.

Host: So how do you decide which lever to pull first?

Guest: You pick the one constraint that, if you removed it, would make three other problems disappear on their own. Everything else is noise you can revisit next quarter. We cut our roadmap by sixty percent last year and shipped more revenue-moving work than in the two years before that combined.

Host: That is a big claim. What actually made the difference?

Guest: Instrumentation before intervention. We refused to change anything we could not measure within seven days. That single rule killed more bad ideas than any strategy document ever written.

Guest: The other thing nobody tells you is that pricing is a product decision, not a finance decision. When we raised prices forty percent, churn did not move at all, and the quality of the conversations our sales team had changed overnight. The customers we lost were the ones we could never have served well anyway.

Host: And the team side of it?

Guest: Small teams win because communication cost is the real tax on execution. Every person you add multiplies the number of conversations required to make a single decision. We kept the core team at nine people deliberately, and that constraint forced a clarity we would never have chosen voluntarily.

Host: Last question. What should someone do on Monday morning after listening to this?

Guest: Write down the one metric you would be embarrassed to show an investor, then spend the whole week finding out why it looks like that. That is the entire job. Everything else is a slide.`;

/* -------------------------------------------------------------------------- */
/*  Text utilities                                                            */
/* -------------------------------------------------------------------------- */

const STOPWORDS = new Set([
  "about", "actually", "after", "again", "against", "almost", "already", "also", "always",
  "another", "anything", "around", "because", "become", "been", "before", "being", "between",
  "both", "called", "came", "come", "could", "didn", "does", "doing", "done", "down", "each",
  "else", "even", "ever", "every", "everything", "from", "gets", "getting", "going", "gonna",
  "guest", "have", "having", "here", "host", "into", "just", "keep", "kind", "know", "least",
  "like", "little", "look", "looking", "made", "make", "making", "many", "mean", "more", "most",
  "much", "must", "need", "never", "next", "nobody", "none", "nothing", "okay", "only", "other",
  "over", "part", "people", "really", "right", "said", "same", "says", "sense", "should", "show",
  "side", "some", "something", "sort", "started", "still", "stuff", "such", "sure", "take",
  "talking", "tell", "than", "that", "their", "them", "then", "there", "these", "they", "thing",
  "things", "think", "this", "those", "thought", "three", "time", "times", "together", "took",
  "trying", "turn", "under", "until", "very", "want", "wanted", "ways", "week", "weeks", "well",
  "went", "were", "what", "when", "where", "which", "while", "will", "with", "without", "words",
  "would", "yeah", "year", "years", "your",
  // podcast meta-talk: real for the recording, useless as a post topic
  "answer", "conversation", "episode", "guest", "listener", "listening", "podcast", "question",
  "recording", "show",
]);

/** Abstract-noun endings: these read better than verbs in "X and Y" topic phrases. */
const NOUNISH = /(?:tion|sion|ment|ity|ance|ence|ness|ship|ing|cy)$/;

const FILLER =
  /^(?:today we|welcome back|thanks for having|thank you for having|last question|and the team|so how|that is a big claim|that's a big claim|what should someone|right\b|yeah\b|okay\b|let's talk|before we|i'll start)/i;

export function lowerFirst(value: string) {
  return value.charAt(0).toLowerCase() + value.slice(1);
}

export function upperFirst(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function titleCase(value: string) {
  return value
    .split(" ")
    .map((word) => upperFirst(word))
    .join(" ");
}

export function clip(value: string, max: number) {
  const trimmed = value.trim();
  if (trimmed.length <= max) return trimmed;
  const cut = trimmed.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  const safe = lastSpace > max * 0.5 ? cut.slice(0, lastSpace) : cut;
  return `${safe.trim().replace(/[,;:.—-]$/, "")}…`;
}

export function countWords(value: string) {
  return value.split(/\s+/).filter(Boolean).length;
}

function stripSpeakers(text: string) {
  return text
    .replace(/\r/g, "")
    .replace(/^\s*(?:\[[^\]]*\]\s*)?[A-Z][A-Za-z .'-]{0,24}:\s*/gm, "")
    .replace(/\s*\[\d{1,2}:\d{2}(?::\d{2})?\]\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function splitSentences(text: string) {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim().replace(/^[-–—•]\s*/, ""))
    .filter(
      (sentence) =>
        sentence.length >= 40 &&
        sentence.length <= 280 &&
        !sentence.endsWith("?") &&
        !FILLER.test(sentence),
    );
}

/** Crude singular form so "teams" and "team" are counted as one concept. */
function stem(word: string) {
  if (word.length > 4 && word.endsWith("ies")) return `${word.slice(0, -3)}y`;
  if (word.length > 4 && word.endsWith("ses")) return word.slice(0, -2);
  if (word.length > 3 && word.endsWith("s") && !word.endsWith("ss")) return word.slice(0, -1);
  return word;
}

function tokenize(text: string) {
  return (text.toLowerCase().match(/[a-z][a-z'-]{2,}/g) ?? []).map(stem);
}

function keywordCounts(text: string) {
  const counts = new Map<string, number>();
  for (const word of tokenize(text)) {
    if (word.length < 4 || STOPWORDS.has(word)) continue;
    counts.set(word, (counts.get(word) ?? 0) + 1);
  }
  return counts;
}

/** Most repeated two-word phrase made of meaningful words, e.g. "product team". */
function topPhrase(text: string) {
  const raw = text.toLowerCase().match(/[a-z][a-z'-]{2,}/g) ?? [];
  const phrases = new Map<string, number>();
  const surface = new Map<string, string>();

  for (let index = 0; index < raw.length - 1; index += 1) {
    const first = stem(raw[index]);
    const second = stem(raw[index + 1]);
    if (first.length < 4 || second.length < 4) continue;
    if (STOPWORDS.has(first) || STOPWORDS.has(second)) continue;
    const key = `${first} ${second}`;
    phrases.set(key, (phrases.get(key) ?? 0) + 1);
    if (!surface.has(key)) surface.set(key, `${raw[index]} ${raw[index + 1]}`);
  }

  const best = [...phrases.entries()]
    .filter(([, count]) => count >= 2)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];

  return best ? (surface.get(best[0]) ?? best[0]) : null;
}

const NUMBER_WORD =
  "ten|eleven|twelve|fifteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred";

function findStat(text: string) {
  const percent = text.match(
    new RegExp(`\\b(?:\\d{1,3}(?:\\.\\d+)?|${NUMBER_WORD})\\s?(?:percent|%)`, "i"),
  );
  if (percent) {
    const value = /^\d/.test(percent[0]) ? percent[0].replace(/\s?percent/i, "%") : percent[0];
    return `${upperFirst(value)}. That's the number from this episode I had to rewind twice.`;
  }

  const multiple = text.match(/\b\d{1,3}(?:\.\d+)?x\b/i);
  if (multiple) {
    return `${multiple[0].toLowerCase()}. That's the number from this episode I had to rewind twice.`;
  }

  return "There's one line in this episode I've already quoted in three separate meetings.";
}

const FALLBACK_INSIGHTS = [
  "The constraint is almost never the thing everyone is already optimising.",
  "Speed is not how fast you ship, it is how fast you learn whether shipping mattered.",
  "If you can't measure a change within a week, you aren't experimenting, you're hoping.",
  "Cutting scope is the only lever that reliably makes everything else cheaper.",
  "The customers you lose when you raise your standards are the ones you could never serve well.",
  "Communication cost is the real tax on execution, and headcount multiplies it.",
  "Write down the metric you'd be embarrassed to show an investor. Start there.",
  "Instrumentation before intervention kills more bad ideas than any strategy document.",
];

function buildContext(rawText: string, length: LengthOption): Context {
  const clean = stripSpeakers(rawText);
  const sentences = splitSentences(clean);
  const counts = keywordCounts(clean);

  const keywords = [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 12)
    .map(([word]) => word);

  const scored = sentences
    .map((sentence) => {
      const words = tokenize(sentence);
      const score =
        words.reduce((total, word) => total + (counts.get(word) ?? 0), 0) /
        Math.sqrt(Math.max(sentence.length, 1));
      return { sentence, score };
    })
    .sort((a, b) => b.score - a.score);

  const seen = new Set<string>();
  const picked: string[] = [];
  for (const { sentence } of scored) {
    const fingerprint = sentence.slice(0, 28).toLowerCase();
    if (seen.has(fingerprint)) continue;
    seen.add(fingerprint);
    picked.push(upperFirst(clip(sentence, 190)));
    if (picked.length >= 8) break;
  }

  const insights = Array.from({ length: 8 }, (_, index) =>
    picked.length > 0
      ? picked[index % picked.length]
      : FALLBACK_INSIGHTS[index % FALLBACK_INSIGHTS.length],
  );

  const phrase = topPhrase(clean);
  const topic = phrase ?? keywords[0] ?? "execution";
  const lead = insights[0].toLowerCase();
  const others = keywords.filter((word) => !topic.includes(word));
  const secondary =
    others.find((word) => NOUNISH.test(word)) ??
    others.find((word) => lead.includes(word)) ??
    others[0];
  const theme = phrase ?? (secondary ? `${topic} and ${secondary}` : topic);

  const quote = clip(insights[0], 170).replace(/…$/, "");

  const wordCount = clean.split(/\s+/).filter(Boolean).length;
  const minutes = Math.round(wordCount / 140);

  return {
    topic,
    theme,
    insights,
    keywords,
    stat: findStat(clean),
    quote,
    duration: minutes >= 15 ? `${minutes} minutes` : "an entire episode",
    rich: length.rich,
    limit: length.limit,
  };
}

function assemble(blocks: string[], tags: string) {
  const body = blocks.map((block) => block.trim()).filter(Boolean);
  let text = `${body.join("\n\n")}\n\n${tags}`;

  while (text.length > LINKEDIN_LIMIT && body.length > 2) {
    body.splice(body.length - 2, 1);
    text = `${body.join("\n\n")}\n\n${tags}`;
  }

  if (text.length > LINKEDIN_LIMIT) {
    text = clip(text, LINKEDIN_LIMIT - 1);
  }

  return text;
}

export function findTone(id: string) {
  return TONES.find((item) => item.id === id) ?? TONES[1];
}

export function findAudience(id: string) {
  return AUDIENCES.find((item) => item.id === id) ?? AUDIENCES[0];
}

export function findLength(id: string) {
  return LENGTHS.find((item) => item.id === id) ?? LENGTHS[1];
}

export interface DraftedPost {
  id: ArchetypeId;
  text: string;
}

/**
 * Deterministic client-side generator. Used when no Anthropic key is configured
 * and as the fallback when the generation request fails.
 */
export function generateLocalPosts(
  transcript: string,
  toneId: string,
  audienceId: string,
  lengthId: string,
): DraftedPost[] {
  const tone = findTone(toneId);
  const audience = findAudience(audienceId);
  const length = findLength(lengthId);
  const ctx = buildContext(transcript, length);
  const tags = [...tone.tags, audience.tag].join(" ");

  return ARCHETYPES.map((archetype) => ({
    id: archetype.id,
    text: assemble(archetype.build(ctx, tone, audience), tags),
  }));
}
